import { useState } from "react";
import { HelpCircle } from "lucide-react";
import { useAuth } from "@/auth/useAuth";
import { changePassword } from "@/api/auth";
import { ApiError } from "@/api/client";
import { OnboardingTour } from "@/onboarding/OnboardingTour";
import { profiloStaffSteps, profiloStaffVersion } from "@/onboarding/profiloStaffSteps";
import { UtenzeFornitoreSezione } from "./UtenzeFornitoreSezione";

const ROLE_LABELS: Record<string, string> = {
  operatore: "Operatore",
  operatore_supervisore: "Operatore Supervisore",
  affiliato: "Affiliato",
  responsabile: "Responsabile Rete",
};

function formatRoles(roles: string[]): string {
  const staff = roles.filter((r) => r !== "cliente");
  // se supervisore presente, nascondi "operatore" (supervisore implica operatore)
  const deduped = staff.includes("operatore_supervisore")
    ? staff.filter((r) => r !== "operatore")
    : staff;
  return deduped.map((r) => ROLE_LABELS[r] ?? r).join(", ");
}

interface ProfiloStaffProps {
  /** "rete": profilo del responsabile di rete — niente utenze fornitore né tour d'agenzia. */
  variante?: "agenzia" | "rete";
}

export function ProfiloStaff({ variante = "agenzia" }: ProfiloStaffProps) {
  const { user, hasRole } = useAuth();
  const isStaff = variante === "agenzia" && (hasRole("affiliato") || hasRole("operatore"));

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [replayNonce, setReplayNonce] = useState(0);

  const isValid =
    currentPassword.length > 0 &&
    newPassword.length >= 8 &&
    newPassword === confirmPassword;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!isValid) return;
    setSaving(true);
    setErrorMsg("");
    setSuccessMsg("");

    try {
      await changePassword(currentPassword, newPassword);
      setSuccessMsg("Password aggiornata con successo.");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.detail === "wrong_current_password") {
          setErrorMsg("La password attuale non è corretta.");
        } else if (err.detail === "password_too_short") {
          setErrorMsg("La nuova password deve essere di almeno 8 caratteri.");
        } else {
          setErrorMsg(`Errore (${err.status}). Riprova.`);
        }
      } else {
        setErrorMsg("Errore nel cambio password. Riprova.");
      }
    } finally {
      setSaving(false);
    }
  }

  if (!user) return null;

  return (
    <div className="max-w-2xl mx-auto space-y-4">
      {variante === "agenzia" && (
        <OnboardingTour
          steps={profiloStaffSteps}
          flagKey="profilo_staff"
          version={profiloStaffVersion}
          replayNonce={replayNonce}
        />
      )}
      {isStaff && (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => setReplayNonce((n) => n + 1)}
            className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-text transition-colors"
            title="Rivedi guida"
          >
            <HelpCircle size={14} />
            <span>Rivedi guida</span>
          </button>
        </div>
      )}
      {successMsg && (
        <div className="bg-green-100 text-green-800 border border-green-200 px-4 py-3 rounded-lg text-sm font-medium">
          {successMsg}
        </div>
      )}
      {errorMsg && (
        <div className="bg-red-100 text-red-800 border border-red-200 px-4 py-3 rounded-lg text-sm">
          {errorMsg}
        </div>
      )}

      {/* Dati profilo — read-only */}
      <div data-tour="profilo-dati" className="bg-card p-6 rounded-lg border border-border">
        <h2 className="text-lg font-semibold text-text mb-1">Profilo</h2>
        <p className="text-xs text-muted-foreground mb-4">I dati del tuo account.</p>
        <div className="grid grid-cols-2 gap-3">
          <ReadOnlyField label="Nome" value={user.name} />
          <ReadOnlyField label="Email" value={user.email} />
          <ReadOnlyField label="Ruolo" value={formatRoles(user.roles)} />
          {user.agencyName && (
            <ReadOnlyField label="Agenzia" value={user.agencyName} />
          )}
        </div>
      </div>

      {/* Utenze fornitore — solo staff di agenzia */}
      {isStaff && <UtenzeFornitoreSezione />}

      {/* Cambio password */}
      <div data-tour="profilo-password" className="bg-card p-6 rounded-lg border border-border">
        <h2 className="text-lg font-semibold text-text mb-4">Cambia password</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Password attuale</label>
            <input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-lg text-sm outline-none focus:border-brand bg-card text-text"
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Nuova password</label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-lg text-sm outline-none focus:border-brand bg-card text-text"
            />
            {newPassword.length > 0 && newPassword.length < 8 && (
              <p className="text-xs text-red-500 mt-1">Minimo 8 caratteri</p>
            )}
          </div>
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Conferma nuova password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-lg text-sm outline-none focus:border-brand bg-card text-text"
            />
            {confirmPassword.length > 0 && newPassword !== confirmPassword && (
              <p className="text-xs text-red-500 mt-1">Le password non coincidono</p>
            )}
          </div>
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={!isValid || saving}
              className="bg-brand text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-brand-dark disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
            >
              {saving ? "Aggiornamento..." : "Cambia password"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ReadOnlyField({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <label className="text-xs text-muted-foreground block mb-1">{label}</label>
      <input
        value={value}
        disabled
        readOnly
        className="w-full px-3 py-2 border border-border rounded-lg text-sm outline-none bg-surface text-muted-foreground"
      />
    </div>
  );
}
